import { FC, useState, useRef, useEffect } from 'react';
import { Heading } from '@atlassian/learning-components';
import { css } from '@compiled/react';

import { Taxonomy } from 'src/pages/CatalogPage/children/Taxonomy';
import { Taxonomy as TaxonomyType } from 'src/pages/CatalogPage/loader';
import { TaxonomyCount } from 'src/pages/CatalogPage/utils/taxonomy';
import { SearchbarWrapper } from 'src/pages/CatalogPage/children/SearchbarWrapper';
import { handleWheel, handleTouch } from 'src/pages/CatalogPage/utils/scrollBoundary';
import {
  useAdobeTaxonomy,
  AdobeTaxonomy,
  adobeTaxonomyToDisplayTextMap,
} from 'src/pages/CatalogPage/hooks/useAdobeTaxonomy';
import { AdobeSearchResponse } from 'src/services/http/adobe';

interface SidebarProps {
  numberEntitiesPerTaxonomy: TaxonomyCount;
  isLoadingCount?: boolean;
  taxonomy: TaxonomyType;
  onFilterInteraction?: () => void;
  onSearchInteraction: () => void;
  isLoadingCatalogResources: boolean;
  isAdobeCatalog?: boolean;
  onAdobeSearchResults?: (results: AdobeSearchResponse | null) => void;
  onAdobeSearchStart?: () => void;
}

export const taxonomyToDisplayTextMap: { [key: string]: string } = {
  type: 'Type',
  duration: 'Duration',
  learningLevel: 'Level',
  product: 'App',
  productRole: 'Product Role',
  deployment: 'Deployment',
};

export const Sidebar: FC<SidebarProps> = ({
  taxonomy,
  numberEntitiesPerTaxonomy,
  onFilterInteraction,
  isLoadingCount,
  onSearchInteraction,
  isLoadingCatalogResources,
  isAdobeCatalog = false,
  onAdobeSearchResults,
  onAdobeSearchStart,
}) => {
  const sidebarRef = useRef<HTMLDivElement>(null);
  const { adobeTaxonomy, catalogMapping } = useAdobeTaxonomy(isAdobeCatalog);

  useEffect(() => {
    if (!sidebarRef.current) {
      return;
    }

    const element = sidebarRef.current;
    const wheelHandler = (e: WheelEvent): void => handleWheel(element, e);
    const touchHandler = (e: TouchEvent): void => handleTouch(element, e);

    element.addEventListener('wheel', wheelHandler, { passive: false });
    element.addEventListener('touchmove', touchHandler, { passive: false });

    return () => {
      element.removeEventListener('wheel', wheelHandler);
      element.removeEventListener('touchmove', touchHandler);
    };
  }, []);

  const activeTaxonomy = isAdobeCatalog ? adobeTaxonomy : taxonomy;
  const activeTaxonomyMap = isAdobeCatalog ? adobeTaxonomyToDisplayTextMap : taxonomyToDisplayTextMap;

  const [expandedTaxonomies, setExpandedTaxonomies] = useState<Record<string, boolean>>(
    Object.keys(activeTaxonomy).reduce(
      (acc, taxonomy) => {
        acc[taxonomy] = true;

        return acc;
      },
      {} as Record<string, boolean>,
    ),
  );

  const handleExpand = (taxonomyKey: string): void => {
    setExpandedTaxonomies((prev) => ({
      ...prev,
      [taxonomyKey]: !prev[taxonomyKey],
    }));
  };

  return (
    <aside ref={sidebarRef} css={sidebarStyles} data-testid="catalog-page-sidebar">
      <div css={filtersContainer}>
        <SearchbarWrapper
          onSearchInteraction={onSearchInteraction}
          isLoadingResources={isLoadingCatalogResources}
          isAdobeCatalog={isAdobeCatalog}
          onAdobeSearchResults={onAdobeSearchResults}
          onAdobeSearchStart={onAdobeSearchStart}
        />
        <div css={css({ margin: '32px 0' })}>
          <Heading level={6}>Filter by</Heading>
        </div>
        {Object.keys(activeTaxonomy).map((t) => {
          const filters = isAdobeCatalog
            ? (activeTaxonomy as AdobeTaxonomy)[t as keyof AdobeTaxonomy]
            : (activeTaxonomy as TaxonomyType)[t as keyof TaxonomyType];

          return (
            !!filters.length && (
              <Taxonomy
                key={t}
                isMobile={false}
                taxonomy={activeTaxonomyMap[t]}
                queryParamKey={t}
                filters={filters}
                filterCounts={numberEntitiesPerTaxonomy[t]}
                isLoadingCount={isLoadingCount}
                isExpanded={expandedTaxonomies[t]}
                handleExpand={() => handleExpand(t)}
                onFilterInteraction={onFilterInteraction}
                shouldShowAllFilters
                isAdobeCatalog={isAdobeCatalog}
                catalogMapping={t === 'catalog' ? catalogMapping : undefined}
              />
            )
          );
        })}
      </div>
    </aside>
  );
};

const sidebarStyles = css({
  width: '305px',
  paddingTop: '16px',
  position: 'sticky',
  top: '136px',
  maxHeight: 'calc(100vh - 136px)',
  overflowY: 'scroll',
  overflowX: 'hidden',
});

const filtersContainer = css({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'flex-start',
});
